import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import { boundedNote } from "../board.ts";
import type { Session } from "../session.ts";
import {
  listingNote,
  PROVISION_REFUSAL,
  statesPresent,
  stateOf,
  tenantRow,
  TENANTS_SCOPE,
  type Tenant,
  type TenantStatus,
} from "../tenants.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `tenants` in `16 WP15 Platform Machinery.dc.html`: every business on the platform.
 *
 * ```
 * tenants:['Tenant list and provisioning','TEN-06',
 *   'Every business on the platform. Provisioning is a saga; a failed step compensates instead of
 *    leaving a half-made tenant.',
 *   [['Tenants','5','done','4 active'],['Provisioning','1','active','running'],
 *    ['Suspended','1','closed','billing'],['Failed sagas','0','done','none']],
 *   ['Tenant','ID','Region'],
 *   [['East Africa Mobility Ltd','ten_7Q2M','KE · UG · TZ','done','active'],
 *    ['Harbour Pharmacy Group','ten_5HP2','TZ','active','provisioning · step 4 of 6'],
 *    ['Sahel Grocers SA','ten_B04N','SN · ML','closed','suspended']],
 *   'info','Opening a tenant switches context with a confirmation naming both sides.']
 * ```
 *
 * **This page's absence was the one that made this console's whole shape.** `navigation.ts` recorded
 * it as "POST /v1/tenants exists; there is no GET, so the board that discovers tenants cannot list
 * them" — and that is also why `tenantId` is not a free parameter anywhere else in that file: every
 * other read on this console needs an identifier this page was supposed to supply. TEN-01 publishes
 * the listing now.
 *
 * What is drawn from data and what is not — `../tenants.ts` argues each:
 *
 * | drawn                                   | source                                                      |
 * | --------------------------------------- | ----------------------------------------------------------- |
 * | the rows                                | TEN-01 `GET /v1/tenants`                                    |
 * | `Tenant` — `East Africa Mobility Ltd`   | `displayName`, a real name rather than an identifier        |
 * | `ID` — `ten_7Q2M`                       | `id`. The design's short code does not exist (R-11)         |
 * | `Region` — `KE · UG · TZ`               | **nothing** — swept across every contract, drawn absent     |
 * | `provisioning · step 4 of 6`            | the status; **the step has no source** this page can reach  |
 * | the four tiles                          | no listing publishes a total (R-04); no tile row here       |
 * | the primary command                     | drawn and refused — `PROVISION_REFUSAL`                     |
 *
 * **The source chip is the design's own `TEN-06` and the service this calls is TEN-01.** That is
 * the design's copy and `CLAUDE.md` §1 governs it; editing it would be redrawing the design to
 * match the build. TEN-06 is the provisioning saga, which is what the page's second half is about
 * and which publishes nothing a browser can bind: its progress read takes a provisioning identifier
 * this page cannot discover, and its write publishes no request body. The footnote under the grid
 * says so, which is the honest form of the caveat.
 *
 * **The design's closing note describes something this console does not do.** "Opening a tenant
 * switches context with a confirmation naming both sides" — a tenant switcher is the shell's, and
 * this console's shell draws one. Opening a row here opens the design's detail panel, which is what
 * every other board on this console does and what `CLAUDE.md` §1 names among the things not to
 * remove. The note is kept word for word regardless, because it is the design's copy.
 */

const tenantRegistry = services.tenant__tenant_registry;

export interface TenantsProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Tenants › Tenant list and provisioning`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Tenant list and provisioning";
const SOURCE = "TEN-06";
const DESCRIPTION =
  "Every business on the platform. Provisioning is a saga; a failed step compensates instead of leaving a half-made tenant.";
const COLUMNS = ["Tenant", "ID", "Region"] as const;
const NOTE = "Opening a tenant switches context with a confirmation naming both sides.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.tenants[0]`, which is the word on its heading button. */
const PRIMARY = "Provision tenant";

/** The most rows one request answers, which is this listing's own maximum. */
const PER_FETCH = 200;

/**
 * Narrow one published row onto the shape this board reads.
 *
 * The generated client types every field, so this is a narrowing of the status alone — and it is
 * here rather than a cast because a cast would assert a guarantee this console did not check. A
 * status the contract's enum does not cover is read as `terminated`, which is the only safe
 * direction: the alternative is a badge with no class, and a tenant whose status this console
 * cannot read is not a tenant it should draw as active.
 */
const STATUSES: readonly TenantStatus[] = ["provisioning", "active", "suspended", "terminated"];

function tenantOf(row: {
  id: string;
  slug: string;
  legalName: string;
  displayName: string;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}): Tenant {
  const status = STATUSES.find((candidate) => candidate === row.status) ?? "terminated";
  return {
    id: row.id,
    slug: row.slug,
    legalName: row.legalName,
    displayName: row.displayName,
    status,
    version: row.version,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

export function Tenants({ transport, session, crumbs }: TenantsProps): ReactNode {
  const [rows, setRows] = useState<readonly Tenant[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * The scope asked for before the call, not discovered from a refusal.
     *
     * `tenant:read:any` is the widest grant this console holds — it reads every tenant on the
     * platform — so an operator who does not have it is told its name rather than shown an empty
     * board. A 403 from the service renders the same state, so this is not the only guard; it is
     * the one that makes the scope a literal in this application's source, which is what
     * `channel-scopes.mjs` derives the Keycloak grant from. ADR 0049 §3.
     */
    if (!session.scopes.has(TENANTS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [TENANTS_SCOPE] });
      return;
    }

    /*
     * One call, and no second one for the provisioning progress.
     *
     * TEN-06's `GET /v1/provisioning/{id}` holds the step the design draws and takes a
     * **provisioning** identifier, which is not a tenant identifier and which nothing on this page
     * can discover. So there is nothing to fan out to, which is why the step is omitted rather
     * than fetched per row — and a per-row fetch would be the fan-out Document 17 WEB-0743
     * tolerates for one listing per screen and not for a column.
     */
    try {
      const page = await tenantRegistry
        .createTenantRegistryClient(transport)
        .listTenants(String(PER_FETCH));
      setRows(page.tenants.map(tenantOf));
      setTruncated(typeof page.nextAfter === "string");
    } catch (error) {
      setFailure(stateForError(error, [TENANTS_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter((tenant) => state === "" || stateOf(tenant).label === state).map(tenantRow);

  const bounded = boundedNote(rows?.length, truncated, "tenants", "newest first");
  const footnote = listingNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No tenant is registered on this platform. A row appears here when one is created with TEN-01."
      primary={{ label: PRIMARY, refusal: PROVISION_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="tenants"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
